import { IPromise, IQService, ITimeoutService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { Observable, defaultIfEmpty, forkJoin, take } from "rxjs";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { IRestLayerRequest } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { CurrentEmployeeActions, ICurrentEmployeeStoreState } from "@gtmhub/employees";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal, IGoalsAndMetrics } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { IHubSessionIdWorkflowMap, IHubWorkflowItemsCollection } from "@gtmhub/hub/models/hub";
import { GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { IChecklist, OnboardingService } from "@gtmhub/onboarding";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { filterActiveSessions, filterOpenedActiveAndFutureSessions } from "@gtmhub/sessions/redux/session-selectors";
import { PlanningSessionService } from "@gtmhub/sessions/services/planning-session.service";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { TaskService } from "@gtmhub/tasks/services/task.service";
import { ITeam, TeamService } from "@gtmhub/teams";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { IdMap, toIdMap } from "@gtmhub/util";
import { ICollection } from "@webapp/core/core.models";
import { ModuleFlag } from "@webapp/feature-toggles/models/feature-module.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { CheckInDTO, CheckInSettingsDTO } from "@webapp/reflections/models/checkins-facade.models";
import { TodoCheckIns } from "@webapp/reflections/models/reflections.models";
import { REFLECTION_CADENCE_DURATION_IN_DAYS } from "@webapp/reflections/models/team-reflections.utils";
import { CheckInsFacade } from "@webapp/reflections/services/checkins-facade.service";
import { getLastDayOfCadence, getLastMondayAsDate } from "@webapp/reflections/utils";
import { Session } from "@webapp/sessions/models/sessions.model";
import { Task } from "@webapp/tasks/models/tasks.models";
import { IHomeMetric } from "../home.models";
import { HomeService } from "../services/home";
import { getEmployeeTeamIds, getEmployeeTeamIdsAndManagedTeamIds, getGoalsToApprove, getNotUpdatedMetricsInCurrentCadence, isToDoMetric } from "../utils";

const bindStateToScope = (state: ISessionsStoreState & ICurrentEmployeeStoreState) => {
  const { isFetched: sessionsFetched, error: sessionsError } = state.sessions;
  const { info: currentEmployee, isFetched: currentEmployeeFetched, error: currentEmployeeError } = state.currentEmployee;

  return {
    reduxStatesInitialized: sessionsFetched && currentEmployeeFetched,
    reduxStateError: sessionsError || currentEmployeeError,
    employee: currentEmployee,
  };
};

type IHomeItem = "todo" | "myOkrs";

export type IHomeTeam = Pick<ITeam, "id" | "accountId" | "name" | "picture" | "color" | "avatar" | "isActive">;

export interface IHomeCollapsedItems {
  todo: boolean;
  myOkrs: boolean;
  myTeamsOkrs: Record<string, boolean>;
}

export interface IHomeTodoItems {
  tasks: Task[];
  metrics: IHomeMetric[];
  goalsToApprove: IGoal[];
}

export interface IDashboardCtrlScope extends IModalScope, IExtendedRootScopeService, ReturnType<typeof bindStateToScope> {
  isReflectionsFeatureModuleEnabled: boolean;
  skeletonTemplateUrl: string;
  todoItems: IHomeTodoItems;
  todoReflections: TodoCheckIns[];
  indicators: {
    loading?: IIndicator;
  };
  homeItems: {
    todo: boolean;
    myOkrs: boolean;
  };
  showHomeProgress: boolean;
  myOkrs: IGoal[];
  teams: IHomeTeam[];
  shouldSeeOnboardingExperiment: boolean;
  shouldSeeFirstOkrGuide: boolean;
  shouldSeeHomeFeedButtons: boolean;
  homeCollapsedItems: IHomeCollapsedItems;
  disableNavBarIndicator(loaded: boolean): void;

  toggleSection(item: IHomeItem): void;
  updateHomeCollapsedItems(item: Partial<IHomeCollapsedItems>): void;
  destroyProgressComponent(): void;
  allSessions: Session[];
  allSessionsIds: string[];
  activeSessions: Session[];
  activeOpenSessions: Session[];
  activeSessionIds: string[];
  activeSessionsMap: IdMap<Session>;
}

export class DashboardCtrl implements IStateInit {
  private strings = {
    taskStatuses: ["inprogress", "todo"],
  };

  private allReviewableGoals: IHubWorkflowItemsCollection = null;
  private tasks: ICollection<Task> = null;

  public static $inject = [
    "$scope",
    "$rootScope",
    "$q",
    "CurrentUserService",
    "UIErrorHandlingService",
    "HomeService",
    "GoalService",
    "PlanningSessionService",
    "TaskService",
    "TeamService",
    "$ngRedux",
    "MetricService",
    "PlanningSessionsActions",
    "CurrentEmployeeActions",
    "OnboardingService",
    "$timeout",
    "FeatureTogglesFacade",
    "CheckInsFacade",
    "ApmSpanService",
  ];

  constructor(
    private $scope: IDashboardCtrlScope,
    private $rootScope: ITraceRootScopeService,
    private $q: IQService,
    private currentUserService: CurrentUserService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private homeService: HomeService,
    private goalService: GoalService,
    private planningSessionService: PlanningSessionService,
    private taskService: TaskService,
    private teamService: TeamService,
    private $ngRedux: INgRedux,
    private metricService: MetricService,
    private planningSessionsActions: PlanningSessionsActions,
    private currentEmployeeActions: CurrentEmployeeActions,
    private onboardingService: OnboardingService,
    private $timeout: ITimeoutService,
    private featureToggleFacade: FeatureTogglesFacade,
    private checkInsFacade: CheckInsFacade,
    private apmSpanService: ApmSpanService
  ) {
    this.$scope.indicators = {
      loading: { progress: true },
    };

    this.$scope.todoItems = {
      tasks: [],
      metrics: [],
      goalsToApprove: [],
    };
    this.$scope.todoReflections = [];
    this.$scope.showHomeProgress = true;

    this.$scope.homeItems = {
      todo: true,
      myOkrs: true,
    };

    this.$scope.toggleSection = this.toggleSection;
    this.$scope.updateHomeCollapsedItems = this.updateHomeCollapsedItems;
    this.$scope.destroyProgressComponent = this.destroyProgressComponent;

    const okrEvents = new OkrEvents($scope);
    okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));
    this.$scope.$on("toDoItemsEmptied", this.onToDoItemsEmptied);
    this.$scope.$on("onboarding.home.checklistUpdated", () => this.setHomeScreenButtons());

    const removeCloseFirstOkrGuideListener = this.$rootScope.$on("closeFirstOkrGuide", () => this.checkIfShouldSeeFirstOkrGuide());
    const removeFirstOkrCreatedListener = this.$rootScope.$on("firstOkrCreated", () => this.checkIfShouldSeeFirstOkrGuide());

    const unsubscribeReduxBind = this.$ngRedux.connect(bindStateToScope)(this.$scope);

    this.updateSessionsData();

    const unsubscribeReduxSessions = this.$ngRedux.subscribe(() => {
      this.updateSessionsData();
    });

    this.$scope.$on("$destroy", () => {
      unsubscribeReduxBind();
      unsubscribeReduxSessions();
      removeCloseFirstOkrGuideListener();
      removeFirstOkrCreatedListener();
    });
  }

  public stateInit(): IPromise<unknown> {
    return this.apmSpanService.dataLoadSpan("home-dashboard-init", () => {
      this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
      this.$ngRedux.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());

      return this.apmSpanService
        .dataLoadSpan("session-curr-empl-redux-and-ff", () =>
          this.$q.all([
            this.$scope.watchUntilAndRejectOnError(
              () => this.$scope.reduxStatesInitialized,
              () => this.$scope.reduxStateError
            ),
            this.checkReflectionsFeatureModule(),
          ])
        )
        .then(() => this.initializeData())
        .catch((error) => this.handleError(error));
    });
  }

  private updateSessionsData(): void {
    const state = this.$ngRedux.getState() as ISessionsStoreState;
    const sessions = state.sessions?.items || [];

    if (sessions !== this.$scope.allSessions) {
      this.$scope.allSessions = sessions;
      this.$scope.activeSessions = filterActiveSessions(sessions);
      this.$scope.allSessionsIds = sessions.map((s) => s.id);
      this.$scope.activeOpenSessions = filterOpenedActiveAndFutureSessions(sessions);
      this.$scope.activeSessionIds = this.$scope.activeSessions.map((session) => session.id);
      this.$scope.activeSessionsMap = toIdMap(this.$scope.activeSessions);
    }
  }

  private checkReflectionsFeatureModule = (): IPromise<void> => {
    const defer = this.$q.defer<void>();
    this.featureToggleFacade
      .isFeatureAvailable$(ModuleFlag.Reflections)
      .pipe(take(1))
      .subscribe((enabled) => {
        this.$scope.isReflectionsFeatureModuleEnabled = enabled;
        defer.resolve();
      });

    return defer.promise;
  };

  private getHomeCollapsedItems = (): void => {
    const setting = this.currentUserService.getUserSetting<IHomeCollapsedItems>("homeCollapsedItems");
    const myTeamsOkrs = this.$scope.teams.reduce<Record<string, boolean>>((teamsMap, team: ITeam) => {
      teamsMap[team.id] = typeof setting?.myTeamsOkrs[team.id] === "boolean" ? setting.myTeamsOkrs[team.id] : true;
      return teamsMap;
    }, {});

    this.$scope.homeCollapsedItems = setting ? { ...setting, myTeamsOkrs } : { todo: true, myOkrs: true, myTeamsOkrs };
  };

  private updateHomeCollapsedItems = (item: Partial<IHomeCollapsedItems>): void => {
    this.$rootScope.traceAction("toggle_section", () => {
      const newSetting = { ...this.$scope.homeCollapsedItems, ...item };
      this.$scope.homeCollapsedItems = newSetting;

      this.currentUserService.setUserSetting({ homeCollapsedItems: newSetting });
    });
  };

  private toggleSection = (item: IHomeItem): void => {
    this.$scope.homeItems[item] = !this.$scope.homeItems[item];
  };

  private setHomeScreenButtons(): void {
    const checklist = this.currentUserService.getUserSetting("checklist") as IChecklist;
    this.$scope.shouldSeeOnboardingExperiment = this.onboardingService.shouldSeeOnboardingExperiment();

    this.$scope.shouldSeeHomeFeedButtons = !this.$scope.shouldSeeOnboardingExperiment || checklist?.stepsV1?.createdOkr;
  }

  private initializeData = () => {
    this.$scope.indicators = {
      loading: { progress: true },
    };
    this.setHomeScreenButtons();

    return this.apmSpanService.dataLoadSpan("tasks-review-goals-teams-okrs-reflections", () => {
      const initDataPromises = {
        tasks: this.getTasks(),
        reviewableGoals: this.getReviewableGoals(),
        teams: this.getTeams(),
        okrs: this.getOkrs(),
        reflections: this.getTodoReflections(),
      };
      return this.$q.all(initDataPromises).then(({ okrs, reflections }) => {
        this.setHomeTodoItems(okrs.metrics);
        this.getHomeCollapsedItems();

        this.$scope.todoReflections = reflections;
        this.$scope.shouldSeeFirstOkrGuide = okrs.goals.length && this.onboardingService.shouldSeeFirstOkrGuide();
        this.$rootScope.$broadcast("goalsToApproveLoaded", this.$scope.todoItems.goalsToApprove);
        delete this.$scope.indicators.loading;
      });
    });
  };

  private areReflectionsEnabled = (): boolean => {
    return this.$scope.isReflectionsFeatureModuleEnabled;
  };

  private checkIfShouldSeeFirstOkrGuide = () => {
    this.$scope.shouldSeeFirstOkrGuide = this.onboardingService.shouldSeeFirstOkrGuide();
  };

  /** checks if the metric's parent is private
   * - person X can own private objective and assign a metric to person Y - that may have not have the permissions to see it
   * - same happens when you assign to a team - there could be a member that may have not have the permissions
   *
   * the API in this case returns invalid metric response - { id: "" , accountId: "", ...etc } - by the time this get fixed in API
   * we need to perform a validation otherwise the whole home-screen is blocked
   */
  private isMetricValid = (metric: Metric) => {
    return metric && metric.id && metric.accountId;
  };

  private getOkrs = (): IPromise<IGoalsAndMetrics> => {
    const employeeIdAndTeamIds = getEmployeeTeamIds(this.$scope.employee);
    const mongoMetricsFilter: IRestLayerRequest = {
      filter: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        $and: [{ ownerIds: { $in: employeeIdAndTeamIds } }, { sessionId_allActiveSessions: { $in: [] } }],
      },
      // *****lastCheckinDate must go with latestSnapshotAt otherwise the endpoint doesn't return the field*****
      fields: "id,goalId,name,sessionId,ownerIds,color,attainment,dueDate,lastCheckInDate,latestSnapshotAt,sourceMetricId,goalClosedStatus,orderId",
      limit: -1,
    };

    const metrics = this.metricService.getMetricsV2(mongoMetricsFilter);
    const goals = this.getGoals(employeeIdAndTeamIds);

    return this.$q.all({ metrics: metrics, goals: goals }).then((result) => {
      const metricItems = result.metrics.items.filter(this.isMetricValid);
      this.$scope.myOkrs = this.concatOkrs(result.goals.items, metricItems);

      return {
        goals: result.goals.items,
        metrics: metricItems,
      };
    });
  };

  private getTodoReflections = (): IPromise<TodoCheckIns[]> => {
    const reflectionsDefer = this.$q.defer<TodoCheckIns[]>();

    if (this.areReflectionsEnabled()) {
      forkJoin([this.getCurrentUserCheckInsSettings(), this.getCurrentUserCheckInsForCurrentCadence()])
        .pipe(defaultIfEmpty([null, null]))
        .subscribe({
          next: ([settings, checkIns]) => {
            let reflections = [];
            if (settings?.items && checkIns?.items) {
              const lastMondayAsDate = getLastMondayAsDate();
              const currentCadenceEndDate = getLastDayOfCadence(lastMondayAsDate, REFLECTION_CADENCE_DURATION_IN_DAYS);

              reflections = this.homeService.removeDisabledAndFilledInCheckIns(settings.items, checkIns.items).map((r) => ({
                id: r.templateId,
                teamId: r.teamId,
                dueDate: currentCadenceEndDate,
              }));
            }
            reflectionsDefer.resolve(reflections);
          },
          error: () => reflectionsDefer.resolve([]),
        });
    } else {
      reflectionsDefer.resolve([]);
    }

    return reflectionsDefer.promise;
  };

  private getCurrentUserCheckInsSettings = (): Observable<ICollection<CheckInSettingsDTO>> => {
    const userBelongsToTeamIds = getEmployeeTeamIdsAndManagedTeamIds(this.$scope.employee);

    const updatesFilter = {
      filter: { teamId: { $in: userBelongsToTeamIds } },
    };

    return this.checkInsFacade.getCheckInSettings$(updatesFilter);
  };

  private getCurrentUserCheckInsForCurrentCadence = (): Observable<ICollection<CheckInDTO>> => {
    const lastMondayAsDate = getLastMondayAsDate();
    const currentCadenceStartDate = lastMondayAsDate;
    const currentCadenceEndDate = getLastDayOfCadence(lastMondayAsDate, REFLECTION_CADENCE_DURATION_IN_DAYS);
    const userBelongsToTeamIds = getEmployeeTeamIdsAndManagedTeamIds(this.$scope.employee);

    const updatesFilter = {
      filter: {
        $and: [
          { createdAt: { $gte: currentCadenceStartDate } },
          { createdAt: { $lte: currentCadenceEndDate } },
          { teamId: { $in: userBelongsToTeamIds } },
          { userId: getCurrentUserId() },
        ],
      },
    };

    return this.checkInsFacade.getCheckIns$(updatesFilter);
  };

  private setHomeTodoItems = (metrics: Metric[]): void => {
    this.$scope.todoItems = this.getEmployeeTodoItems(metrics.filter(isToDoMetric), this.tasks.items, this.allReviewableGoals.hubWorkflowItems);
  };

  private getReviewableGoals = (): IPromise<IHubWorkflowItemsCollection | void> => {
    const approvalWorkflowItems = "hubWorkflowItems{goalsToReview{id,name,currentUserApproved},itemsToReview}";

    return this.planningSessionService.getCurrentUserWorkflowItems(approvalWorkflowItems, "-approved,draft").then((allReviewableGoals) => {
      this.allReviewableGoals = allReviewableGoals;
    });
  };

  private getTasks = (): IPromise<ICollection<Task> | void> => {
    const mongoTaskFilter: IRestLayerRequest = {
      filter: {
        $and: [
          { ownerId: this.$scope.employee.id },
          { status: { $in: this.strings.taskStatuses } }, // todo and in progress tasks only
          // eslint-disable-next-line @typescript-eslint/naming-convention
          { $or: [{ sessionId_allActiveSessions: { $in: [] } }, { sessionId: { $exists: false } }] },
          { $or: [{ archived: { $exists: false } }, { archived: false }] },
        ],
      },
      fields: "dueDate,name",
    };

    return this.taskService.getTasksV2(mongoTaskFilter).then((tasks) => {
      this.tasks = tasks;
    });
  };

  private getTeams = (): IPromise<ITeam[] | void> => {
    const teamIds = getEmployeeTeamIds(this.$scope.employee);

    if (teamIds.length === 0) {
      this.$scope.teams = [];
      return;
    }

    // get teams from assignees store if fetched
    const store = this.$ngRedux.getState<IAssigneesStoreState>();
    if (store.assignees.isFetched) {
      this.$scope.teams = teamIds.reduce((teams, teamId) => {
        const assignee = store.assignees.map[teamId];
        if (assignee?.type === "team") {
          teams.push(assignee);
        }

        return teams;
      }, [] as IHomeTeam[]);

      return;
    }

    const mongoTeamFilter = {
      filter: {
        _id: { $in: teamIds },
      },
      fields: "name,picture,color,avatar,isActive",
    };

    return (
      this.teamService
        .getTeams(mongoTeamFilter)
        .then((teams: ICollection<IHomeTeam>) => {
          this.$scope.teams = teams.items;
        })
        // catch is needed here as getTeams method is not returned where invoked
        .catch((error) => this.handleError(error))
    );
  };

  private getGoals = (employeeIdAndTeamIds: string[]): IPromise<ICollection<IGoal>> => {
    // getting goal by metric.goalId which we know the metrics are for current active sessions
    // or we are getting them by ownerId where we pass activeSessionIds
    const mongoGoalFilter: IRestLayerRequest = {
      filter: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        $or: [{ id_myMetricsGoals: { $in: [] } }, { $and: [{ ownerIds: { $in: employeeIdAndTeamIds } }, { sessionId_allActiveSessions: { $in: [] } }] }],
      },
      fields: "attainment,name,ownerIds,sessionId,closedStatus,parentId,parentType,private",
      sort: "closedStatus.status,duration,name",
    };

    return this.goalService.getGoalsV2(mongoGoalFilter);
  };

  private concatOkrs = (goals: IGoal[], metrics: Metric[]): IGoal[] => {
    const allGoalIds: string[] = [];

    const allGoals = goals.map((goal) => {
      allGoalIds.push(goal.id);
      return { ...goal, metrics: metrics.filter((metric) => metric.goalId === goal.id) || [] };
    });
    const shadowGoal: IGoal = {
      id: "",
      ownerIds: [],
      metrics: [],
    };
    shadowGoal.metrics = metrics.filter((metric) => !allGoalIds.length || !allGoalIds.includes(metric.goalId));

    return shadowGoal.metrics.length ? [...allGoals, shadowGoal] : allGoals;
  };

  private getEmployeeTodoItems = (metrics: Metric[], tasks: Task[], allReviewableGoals: IHubSessionIdWorkflowMap): IHomeTodoItems => {
    return {
      metrics: getNotUpdatedMetricsInCurrentCadence(metrics, this.$scope.activeSessionsMap, this.$scope.employee), // filtering metrics not updated in current cadence
      tasks, // tasks are already filtered by deadline from API
      goalsToApprove: getGoalsToApprove(allReviewableGoals, this.$scope.allSessions), // filtering goals to review which user hasn't approved yer
    };
  };

  private onToDoItemsEmptied = (): void => {
    this.$scope.todoItems = {
      tasks: [],
      metrics: [],
      goalsToApprove: [],
    };
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    this.$scope.shouldSeeHomeFeedButtons = true;

    if (args.reason === "goalStatusChanged") {
      const oldTodoMetrics = this.$scope.todoItems.metrics.slice();
      const oldMyOkrs = this.$scope.myOkrs.slice();

      // we cannot rely on binding change because bound variables are not used in todo and my-okrs views
      // other collections are iterated in child directives
      // $watches in child scopes may be triggered by other changes so custom events are broadcasted to notify change
      this.getOkrs().then((okrs) => {
        this.setHomeTodoItems(okrs.metrics);

        // timeout is needed because of array value reassigning - this.$scope.myOkrs
        this.$timeout(() => {
          this.$scope.$broadcast("homeTodoItemsUpdated", { oldTodoMetrics });
          this.$scope.$broadcast("homeMyOkrsUpdated", { oldMyOkrs });
        });
      });
    }
  };

  private handleError = <E>(error: E): void => {
    this.uiErrorHandlingService.handleModal(error);
  };

  private destroyProgressComponent = (): void => {
    this.$scope.showHomeProgress = false;
  };
}
